/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import type {
  Compset,
  Hotel,
  Hotelinfo,
  Market,
  Ota,
  User,
} from 'frontend-utils/models';
import {
  type Platform,
  PLATFORMS,
  RATES_DATA_START,
  TYPE_MARKETRATES,
  TYPE_PARITY,
  TYPE_PARITY_INSIGHT,
  TYPE_RATES,
} from 'frontend-utils/utils/constants/constant-values';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import {
  getLocalTime,
  toLocalTime,
  toLocalUnixTime,
} from 'frontend-utils/utils/dayjs';
import {
  isMyHotelsId,
  translateMyHotelsName,
} from 'frontend-utils/utils/my-hotel-utils';
import Cookies from 'js-cookie';
import type { Rate } from 'lumen/models';
import type { PeriodRate } from 'lumen/models/period';
import type AjaxService from 'lumen/services/ajax';
import type { FormatOptions } from 'lumen/services/rates';
import {
  MARKET_PRODUCT_ORDER,
  type MembershipType,
  MEMBERSHIPTYPE_TRANSLATION_KEYS,
  PRODUCT_NAME_TRANSLATION_KEYS,
  SUBSCRIPTION_PRODUCT_ORDER,
  SUBSCRIPTION_TYPE_TRANSLATION_KEYS,
  type SupportedProductTranslation,
  type SupportedSubscriptionTypeTranslation,
} from 'lumen/utils/constants/general-constants';
import { timeout } from 'lumen/utils/waiters';

declare global {
  interface Window {
    hotelId?: string;
    marketId?: string;
  }
}

const INVOICE_OVERDUE_TIME = 30; // Reduced to 30 days

export type HotelOrMarket = Hotel | Market;

export interface RateLike {
  currency: SupportedCurrencies;
  message: string;
  value: number;
}

type HotelOrMarketAutocompleteResult = {
  brand_code: string;
  description: string;
  hotel_id: string | undefined;
  market_id: string | undefined;
  name: string;
  products: string[] | undefined;
  subscription_type: string | undefined;
};

export type NavAutocompleteResult = {
  brand_code?: string;
  description: string;
  destination_subscription_id?: number;
  hotelinfo_id?: number;
  id?: number;
  market_id?: string;
  name: string;
  owner_email?: string | null;
  plans?: string[];
  products?: string[];
  subscription_ids?: number[];
  type?: 'hotel' | 'hotelinfo' | 'market' | 'destinationsubscription';
};

export default class HotelAndMarketService extends Service {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare rates: Services['rates'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked activeCompset: Compset | null | undefined = null;
  @tracked hotel: Hotel | null = null;
  @tracked lastUpdateTime = new Date().getTime();
  // loading is added to avoid https://github.com/emberjs/ember.js/issues/14708 when doing unload on store
  @tracked loading = true;
  @tracked market: Market | null = null;
  @tracked declare user: User;

  /**
   * Retrieve payments info and show a message if there are overdue invoices
   */
  checkPaymentsInfoTask = task(
    { restartable: true },
    async (): Promise<void> => {
      const paymentsInfo = this.getPaymentsInfoEndpointDetails();
      try {
        type PaymentsInfoResponse = {
          overdue_invoices: {
            due_date: string;
            has_overdue_invoices: boolean;
          };
        };
        const res = await this.ajax.request<PaymentsInfoResponse>(
          paymentsInfo.url,
          {
            method: 'GET',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
          },
        );

        if (
          res.overdue_invoices.has_overdue_invoices &&
          dayjs.utc().diff(dayjs.utc(res.overdue_invoices.due_date), 'day') >
            INVOICE_OVERDUE_TIME
        ) {
          this.toast.alert(this.intl.t('invoices.overdue_message'), {
            title: this.intl.t('invoices.overdue_title'),
            showIcon: true,
            sticky: true,
            dismissible: true,
          });
        }
      } catch (error) {
        this.errorTracking.captureMessage('Could not fetch overdue invoices', {
          error,
        });
      }
      // Timeout at the end because you want to run the request immediately
      await timeout();
    },
  );

  @computed('activeCompset.competitors.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeCompetitors() {
    return [...(this.activeCompset?.competitors ?? [])];
  }

  /**
   * Creates a list of all unique competitors across the all compsets
   * This list is ordered in the same way that you would get to see it in the competitor settings
   * @return {array}     Array of hotelinfo models
   */
  @computed('availableCompsets.@each.id', 'hotel.sortedCompsets')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allUniqueAndOrderedCompetitors() {
    const sortedCompsets = this.hotel?.sortedCompsets ?? [];

    const primaryCompset = sortedCompsets.find(({ isPrimary }) => isPrimary);
    const secondaryCompset = sortedCompsets.find(
      ({ isSecondary }) => isSecondary,
    );
    const rmsCompset = sortedCompsets.find(({ isRMS }) => isRMS);

    const compsets = [primaryCompset, secondaryCompset, rmsCompset].filter(
      Boolean,
    ) as Compset[];

    return this.mergeAndOrderCompsets(compsets);
  }

  @computed('hotel.sortedCompsets.@each.isRMS')
  get availableCompsets(): Compset[] {
    const sortedCompsets = this.hotel?.sortedCompsets;

    if (!sortedCompsets) {
      return [];
    }

    return sortedCompsets.filter((compset) => {
      return compset.isRMS === false;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get biRedirectId() {
    return this.hotel?.unifiedhotelview?.biRedirectId;
  }

  @computed('hotel.secondaryCompset.competitors.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSecondaryCompset() {
    return (
      this.hotel?.secondaryCompset &&
      [...(this.hotel?.secondaryCompset?.competitors ?? [])].length > 0
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotel ? this.hotel : (this.market as Market);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDemoModeActive() {
    return this.isDemoMode() !== undefined;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMyHotels() {
    return isMyHotelsId(this.market?.id);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelAndAllCompetitors() {
    if (!this.hotel) {
      return [];
    }

    return [this.hotel.hotelinfo, ...this.primaryAndSecondaryCompetitors];
  }

  @computed('activeCompetitors.[]', 'hotel.hotelinfo')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelAndCompetitors() {
    if (!this.hotel || !this.activeCompetitors) {
      return [];
    }

    return [this.hotel.hotelinfo, ...this.activeCompetitors];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelAndPrimaryCompetitors() {
    if (!this.hotel || !this.primaryCompetitors) {
      return [];
    }

    return [this.hotel.hotelinfo, ...this.primaryCompetitors];
  }

  /**
   * Creates a list of all unique competitors across the primary and secondary compsets
   * This list is ordered in the same way that you would get to see it in the competitor settings
   * @return {array}     Array of hotelinfo models
   */
  @dependentKeyCompat
  get primaryAndSecondaryCompetitors(): Hotelinfo[] {
    const compsets = this.hasSecondaryCompset
      ? [this.primaryCompset, this.secondaryCompset]
      : [this.primaryCompset];

    const filteredCompsets = compsets.filter(Boolean) as Compset[];

    return this.mergeAndOrderCompsets(filteredCompsets);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryCompetitors() {
    return [...(this.primaryCompset?.competitors ?? [])];
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryCompset() {
    return this.hotel?.primaryCompset;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateiRedirectId() {
    return this.hotel?.unifiedhotelview?.rateiRedirectId;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesDataEnd() {
    return this.getLocalTime().add(1, 'year');
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesDataStart() {
    return this.toLocalTime(RATES_DATA_START);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get secondaryCompetitors() {
    if (this.hasSecondaryCompset) {
      return [...(this.secondaryCompset?.competitors ?? [])];
    }
    return [];
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get secondaryCompset() {
    return this.hotel?.secondaryCompset;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timezone() {
    if (this.hotel) {
      return this.hotel?.hotelinfo?.timezone;
    }
    return (this.market as Market)?.timezone;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timezoneName() {
    if (this.hotel) {
      return this.hotel?.hotelinfo?.timezoneName;
    }
    return this.market?.timezoneName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get useImperialSystem() {
    return this.user.locale === 'en-us';
  }

  checkPaymentsInfo(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.checkPaymentsInfoTask.perform();
  }

  /**
   * Clears certain records from the store
   * Some can not be removed as these will provide issues due to relationships:
   *   - roomtypes: will result in request being sent for missing types
   *   - shopconfig, compset, hotel, hotelinfo, market: interferes with functions defined here (hotel will be null, undefined,...)
   *   - liveupdate: can be inflight if the response has not returned, this can not be unloaded
   *   - period: Unloading periods breaks switching My Hotels market rates in Ember data 4.7
   */

  clearStore(): void {
    // get(this, 'store').unloadAll('roomtype');
    this.store.unloadAll('reportconfig');
    this.store.unloadAll('reportgroup');
    // get(this, 'store').unloadAll('shopconfig');
    this.store.unloadAll('occupancy');
    this.store.unloadAll('holiday');
    this.store.unloadAll('event');
    this.store.unloadAll('benchmark');
    this.store.unloadAll('demand');
    this.store.unloadAll('parity');
    this.store.unloadAll('rate');
    this.store.unloadAll('ratesummary');
    this.store.unloadAll('rankingsummary');
    // this.store.unloadAll('liveupdate');
    // this.store.unloadAll('period');
    this.store.unloadAll('outlier');
    this.store.unloadAll('day');
    // this.store.unloadAll('compset');
    this.store.unloadAll('userproductaccess');
    /*
    this.store.peekAll<Hotelinfo>('hotelinfo').forEach((hotelinfo) => {
      if (hotelinfo.get('id') !== get(this, 'hotelinfo.id')) {
        get(this, 'store').unloadRecord(hotelinfo);
      }
    });
    this.store.peekAll<Hotel>('hotel').forEach((hotel) => {
      if (hotel.get('id') !== get(this, 'hotel.id')) {
        get(this, 'store').unloadRecord(hotel);
      }
    });
    this.store.peekAll<Market>('market').forEach((market) => {
      if (market.get('id') !== get(this, 'market.id')) {
        get(this, 'store').unloadRecord(market);
      }
    });
    */
    this.store.peekAll<User>('user').forEach((user) => {
      if (user?.id !== this.user?.id) {
        this.store.unloadRecord(user);
      }
    });
  }

  // time in seconds since epoch
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  findIndexById(collection: { id: unknown }[], id: unknown) {
    let result = -1;

    collection.some((item, index) => {
      if (item.id === id) {
        result = index;
        return true;
      }
      return false;
    });

    return result;
  }

  // Support translations for "My Hotels" in the autocomplete API result
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatAutocompleteResults(
    result: HotelOrMarketAutocompleteResult[],
    formatName = false,
  ) {
    return result.map((hotelOrMarket) => {
      let {
        // eslint-disable-next-line prefer-const
        brand_code,
        description,
        // eslint-disable-next-line prefer-const
        hotel_id,
        // eslint-disable-next-line prefer-const
        market_id,
        name,
        products,
        subscription_type,
      } = hotelOrMarket;

      if (isMyHotelsId(market_id)) {
        description = translateMyHotelsName(description, this.intl);
        name = translateMyHotelsName(name, this.intl);
      }

      if (formatName) {
        name = brand_code ? `${brand_code}-${name}` : name;
      }

      const productsOrder = hotel_id
        ? SUBSCRIPTION_PRODUCT_ORDER
        : MARKET_PRODUCT_ORDER;

      products = products
        ? products
            .filter((product): product is SupportedProductTranslation => {
              return product in PRODUCT_NAME_TRANSLATION_KEYS;
            })
            .sort((a, b) => {
              const aIndex = productsOrder.indexOf(a);
              const bIndex = productsOrder.indexOf(b);

              if (aIndex === -1) {
                return 1;
              }
              if (bIndex === -1) {
                return -1;
              }
              return aIndex - bIndex;
            })
            .map((product) => {
              return this.intl.t(PRODUCT_NAME_TRANSLATION_KEYS[product]);
            })
        : [];

      subscription_type =
        subscription_type &&
        SUBSCRIPTION_TYPE_TRANSLATION_KEYS[
          subscription_type as SupportedSubscriptionTypeTranslation
        ]
          ? this.intl.t(
              SUBSCRIPTION_TYPE_TRANSLATION_KEYS[
                subscription_type as SupportedSubscriptionTypeTranslation
              ],
            )
          : undefined;

      return {
        ...hotelOrMarket,
        description,
        name,
        products,
        subscription_type,
      };
    });
  }

  formatNavAutocompleteResults(
    results: NavAutocompleteResult[],
    formatName = false,
  ): NavAutocompleteResult[] {
    return results.map((result) => {
      // Translate "My Hotels" market names
      if (isMyHotelsId(result.market_id?.toString())) {
        result.name = translateMyHotelsName(result.name, this.intl);
        result.description = translateMyHotelsName(
          result.description,
          this.intl,
        );
      }

      if (formatName && result.brand_code) {
        result.name = result.brand_code
          ? `${result.brand_code}-${result.name}`
          : result.name;
      }

      const productsOrder = result.hotelinfo_id
        ? SUBSCRIPTION_PRODUCT_ORDER
        : MARKET_PRODUCT_ORDER;

      result.products =
        result.products
          ?.filter(
            (product): product is SupportedProductTranslation =>
              product in PRODUCT_NAME_TRANSLATION_KEYS,
          )
          .sort((a, b) => {
            const aIndex = productsOrder.indexOf(a);
            const bIndex = productsOrder.indexOf(b);
            if (aIndex === -1) {
              return 1;
            }
            if (bIndex === -1) {
              return -1;
            }
            return aIndex - bIndex;
          })
          .map((product) =>
            this.intl.t(PRODUCT_NAME_TRANSLATION_KEYS[product]),
          ) ?? [];

      return result;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRate(
    rate: number | RateLike | null,
    includeCurrencySymbol = true,
    longFormat = true,
    format: FormatOptions = {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    },
    decimals = false,
    useRevenueInsightCurrency = false,
    useOverviewRevenueCurrency = false,
  ) {
    return this.rates.formatRate(
      rate,
      this.hotelOrMarket,
      includeCurrencySymbol,
      longFormat,
      format,
      decimals,
      useRevenueInsightCurrency,
      useOverviewRevenueCurrency,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRateChange(
    rate: Rate | number,
    currency: SupportedCurrencies,
    longFormat = true,
    format: FormatOptions = {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    },
    decimals = false,
  ) {
    return this.rates.formatRateChange(
      rate,
      this.hotelOrMarket,
      currency,
      longFormat,
      format,
      decimals,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRateObject(
    rate: Rate | PeriodRate,
    includeCurrencySymbol = true,
    longFormat = true,
    format: FormatOptions = {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    },
  ) {
    return this.rates.formatRateObject(
      rate,
      includeCurrencySymbol,
      longFormat,
      format,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getLocalTime() {
    return getLocalTime(this.timezone);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getLocalTimeWithFallback() {
    // When looking at a RI demo subscription we want to use the freeze date as today's date
    if (
      this.hotel?.isDemoProperty &&
      this.router.currentRouteName?.indexOf('revenue-insight') !== -1
    ) {
      return this.hotel.revenueInsightDemoFreezeDate;
    }

    return this.getLocalTime();
  }

  /**
   * creates URL for fetching overdue invoices
   * @return { object } Object containing the url, type and id of the payments info call
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getPaymentsInfoEndpointDetails() {
    if (this.hotel) {
      const hotelId = this.hotel?.id;
      return {
        url: `${this.ajax?.host}/apigateway/v1/app/payment/info?hotel_id=${hotelId}`,
        type: 'hotel',
        id: hotelId,
      };
    }
    const marketId = this.market?.id;
    return {
      url: `${this.ajax?.host}/apigateway/v1/app/payment/info?market_id=${marketId}`,
      type: 'market',
      id: marketId,
    };
  }

  /**
   * Return a string with the reason why reports can not be scheduled, or an empty string
   * @param  {string} type : which view is active, so rates, parity or marketrates
   * @param  {string} ota  :OTA object
   * @param  {integer} los
   * @return {string}  represents the message to be displayed when no report is avalable to schedule
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getReportSchedulingMessage(
    type: string,
    los: number,
    ota: Ota | null,
    membershipType: MembershipType,
    persons: number,
    platform: Platform = PLATFORMS.PLATFORM_UNSPECIFIED,
  ) {
    if (platform > PLATFORMS.PLATFORM_DESKTOP) {
      return this.intl.t('reports.no_report_available_platform');
    }
    if (membershipType > 1) {
      const translatedMembershipType = this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[membershipType],
      );
      return this.intl.t('reports.no_membertype', {
        membershipType: translatedMembershipType,
      });
    }
    if (
      type === TYPE_RATES ||
      type === TYPE_PARITY ||
      type === TYPE_PARITY_INSIGHT
    ) {
      if (
        type === TYPE_RATES &&
        this.hotel &&
        ota &&
        ota.id &&
        !this.hotel.canScheduleReportForOTA(ota.id)
      ) {
        return this.intl.t('reports.no_report_available_ota');
      }
      let shopconfigs = [...(this.hotelOrMarket?.shopconfigs ?? [])];
      if (type === TYPE_RATES && ota) {
        shopconfigs = shopconfigs.filter((shop) => {
          return (
            shop?.otas?.map((shopOta) => shopOta.id).indexOf(ota.id) !== -1 &&
            ota?.supportedMoments?.includes(shop.moment)
          );
        });
      }
      if (shopconfigs.length) {
        const shopsFilteredByType = shopconfigs.filter(
          (shop) => shop.shopType === type,
        );
        if (shopsFilteredByType) {
          const shopsFilteredByLos = shopsFilteredByType.filter(
            (shop) => shop.los === los,
          );
          if (shopsFilteredByLos.length) {
            // eslint-disable-next-line max-depth
            if (
              type === TYPE_RATES &&
              this.hotel?.shop1And2Persons &&
              ['1', '2'].indexOf(persons.toString()) !== -1
            ) {
              return '';
            }

            // eslint-disable-next-line max-depth
            if (type === TYPE_RATES && ota?.hasMultipleGuestsRequest) {
              return '';
            }

            const shopsFilteredByPersons = shopsFilteredByLos.filter(
              (shop) => shop.persons.toString() === persons.toString(),
            );
            // eslint-disable-next-line max-depth
            if (shopsFilteredByPersons.length) {
              return '';
            }
            return this.intl.t('reports.no_report_available_persons');
          }
          return this.intl.t('reports.no_report_available_los');
        }
      }
      return this.intl.t('reports.no_report_available');
    }
    if (type === TYPE_MARKETRATES && this.market) {
      if (!this.market.defaultLosRates.includes(los)) {
        return this.intl.t('reports.no_report_for_market_los');
      }

      // The current persons QP needs to be in the list of allowed persons or the OTA needs to shop multiple guests per request #PERSONS-SR
      if (
        !this.market.persons.includes(persons) &&
        !ota?.hasMultipleGuestsRequest
      ) {
        return this.intl.t('reports.no_report_available_persons');
      }
    }

    return '';
  }

  /**
   * Checks if the Pace Tab is available on a given date
   * Pace is always available for subscriptions with historic pace and starting from 30 days after the integration date for subscriptions without historic pace
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  hasPaceOnDate(date: Dayjs | string) {
    // Check if we are after the first day we have OTB data for
    const isTodayAfterFirstCompleteStayDate =
      this.hotel?.pmsFirstCompleteStayDateWithDefault.isSameOrBefore(
        date,
        'day',
      );

    if (!isTodayAfterFirstCompleteStayDate) {
      return false;
    }

    const hasHistoricPace = this.hotel?.pmsHasHistoricPace;
    const oneMonthAfterIntegrationDate = dayjs(
      this.hotel?.pmsIntegrationDate,
    ).add(30, 'day');
    // Don't show pace data for the first 30 stay dates after the integration date regardless of today's date
    const isDateOneMonthAfterIntegrationDate =
      oneMonthAfterIntegrationDate.isBefore(date, 'day');
    // If the integration date is less than 30 days ago from today: don't show pace for any stay date
    const isTodayOneMonthAfterIntegrationDate =
      oneMonthAfterIntegrationDate.isBefore(dayjs(), 'day');
    return (
      hasHistoricPace ||
      (isTodayOneMonthAfterIntegrationDate &&
        isDateOneMonthAfterIntegrationDate)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isDemoMode() {
    return Cookies.get('demoModev3');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isHotelinfoMatched(hotelinfo: Hotelinfo, otaId: string) {
    const ownHotelOtaIds = hotelinfo.otas.map((ota) => ota.id);
    return ownHotelOtaIds.indexOf(otaId) !== -1;
  }

  /**
   * Check if the own hotel has a matching for an ota
   * @param { string } otaId  : The ota to check
   * @returns { boolean }     : whether the ota is matched or not
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isHotelMatched(otaId: string | null) {
    if (!this.hotel?.hotelinfo || !otaId) {
      return false;
    }

    return this.isHotelinfoMatched(this.hotel.hotelinfo, otaId);
  }

  // General functions
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isOwnHotelInfo(hotelInfo: Hotelinfo) {
    return hotelInfo?.id === this.hotel?.hotelinfo?.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isPast(date: Dayjs | null) {
    if (date) {
      return date.isBefore(this.getLocalTime(), 'day');
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mergeAndOrderCompsets(compsets: Compset[]) {
    let uniqueCompetitors: Hotelinfo[] = [];

    if (compsets.length === 0) {
      return uniqueCompetitors;
    }

    compsets.forEach((compset) => {
      const competitors = compset.competitors ?? [];

      if (uniqueCompetitors.length === 0) {
        uniqueCompetitors = [...competitors];
        return;
      }

      let newUniqueCompetitors: Hotelinfo[] = [];

      [...competitors].forEach((competitor) => {
        const competitorIndex = this.findIndexById(
          uniqueCompetitors,
          competitor.id,
        );

        if (competitorIndex === -1) {
          newUniqueCompetitors.push(competitor);
        } else {
          // Insert all currently mapped competitors of the second compset before the one that appears in both compsets
          // Can only be secondary compset (Primary or RMS will already be handled by now)
          const startOfUniqueCompetitors = uniqueCompetitors.slice(
            0,
            competitorIndex,
          );

          const endOfUniqueCompetitors =
            uniqueCompetitors.slice(competitorIndex);

          uniqueCompetitors = startOfUniqueCompetitors
            .concat([...newUniqueCompetitors])
            .concat(endOfUniqueCompetitors);

          // Once inserted, we don't need to keep track of the competitors anymore
          newUniqueCompetitors = [];
        }
      });

      if (newUniqueCompetitors.length > 0) {
        uniqueCompetitors = uniqueCompetitors.concat(newUniqueCompetitors);
      }
    });

    return uniqueCompetitors;
  }

  ping(): void {
    const now = new Date().getTime();
    set(this, 'lastUpdateTime', now);
  }

  setActiveCompset(compset: Compset | undefined): void {
    set(this, 'activeCompset', compset);
  }

  setActiveCompsetById(compsetId: string | undefined): void {
    // Search and set the activeCompset with the passed ID. Set the default compset as activeCompset if no ID is passed.
    const compset = compsetId
      ? this.availableCompsets.find((cs) => cs.apiId === compsetId)
      : this.hotel?.sortedCompsets.filter(
          (sortedCompset) => sortedCompset.isPrimary,
        )[0];
    this.setActiveCompset(compset);
  }

  setHotel(hotel: Hotel, queryParams: { compsetId: string }): void {
    const { compsetId } = queryParams;
    window.hotelId = hotel.id ?? undefined;
    set(this, 'hotel', hotel);

    // Use the "compsetId" QP from the URL if available.
    this.setActiveCompsetById(compsetId);

    set(this, 'market', null);
  }

  setMarket(market: Market): void {
    window.marketId = market.id ?? undefined;
    set(this, 'hotel', null);
    set(this, 'activeCompset', null);
    set(this, 'market', market);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toLocalTime(dateString?: string | null) {
    return toLocalTime(dateString, this.timezone);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toLocalTimeWithFallback(dateString: string | undefined | null) {
    if (dateString) {
      return toLocalTime(dateString, this.timezone);
    }
    return getLocalTime(this.timezone);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toLocalUnixTime(unixDate: number) {
    return toLocalUnixTime(unixDate, this.timezone);
  }

  updateActiveCompset(): void {
    this.setActiveCompsetById(this.activeCompset?.apiId);
  }
}

export interface HotelAndMarketServiceWithHotel extends HotelAndMarketService {
  hotel: Hotel;
}

export interface HotelAndMarketServiceWithMarket extends HotelAndMarketService {
  market: Market;
}

declare module '@ember/service' {
  interface Registry {
    'hotel-and-market': HotelAndMarketService;
  }
}
